import { canEnterGatedPhase } from '@projektverwaltung/shared';
/**
 * V1.5 Move-Phase-Modal (Phase 5.11).
 * Quelle: Brief §7.5 + 05_implementation_session2.md §10.1.
 *
 * Flow:
 *   1. User wählt Zielphase aus Dropdown.
 *   2. Falls `entry_required_fields` der Zielphase Lücken im Projekt zeigt,
 *      werden Inline-Editoren für die fehlenden Pfade gerendert.
 *   3. Submit → ggf. PATCH /v1/projects/:id mit gemergten Pflichtfeldern,
 *      anschliessend POST /v1/projects/:id/move-phase.
 *   4. 409 `hard_stop_violation` → Errors pro Feld im Modal (Modal bleibt offen).
 *   5. Erfolg → Toast + Query-Invalidation + onClose().
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { useToast } from '../toast.js';
import { Modal } from './modal.js';

type Phase = {
  phase_id: string;
  name: string;
  order: number;
  entry_required_fields?: string[] | null | undefined;
  required_fields?: string[] | null | undefined;
};

type Project = {
  project_id: string;
  phase_id: string;
  responsibility?: 'PU' | 'Service' | 'MTB' | null | undefined;
  project_lead_user_id?: string | null | undefined;
  manager_user_id?: string | null | undefined;
  mtb_user_id?: string | null | undefined;
  handover_ready_fields?: Record<string, unknown> | null | undefined;
  intake_handover_fields?: Record<string, unknown> | null | undefined;
  [k: string]: unknown;
};

type FieldViolation = {
  field: string;
  reason: 'missing' | 'invalid';
  direction: 'leave' | 'enter';
};

const FIELD_LABELS: Record<string, string> = {
  project_lead_user_id: 'Project Lead (User-ID)',
  manager_user_id: 'Manager (User-ID)',
  mtb_user_id: 'MTB (User-ID)',
  responsibility: 'Bereich (PU / Service / MTB)',
  'handover_ready_fields.restbudget': 'Restbudget',
  'handover_ready_fields.folgethemen': 'Folgethemen',
  'handover_ready_fields.debriefing': 'Debriefing',
  'handover_ready_fields.uebergabe_an_service': 'Übergabe an Service',
  'intake_handover_fields.presales_notes': 'Presales-Notizen',
  'intake_handover_fields.angebot_link': 'Angebots-Link',
  'intake_handover_fields.angebot_besonderheiten': 'Angebots-Besonderheiten',
  'intake_handover_fields.intake_meeting_notes': 'Intake-Meeting-Notizen',
};

function prettyField(path: string): string {
  return FIELD_LABELS[path] ?? path;
}

function readPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const part of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function isFilled(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return Boolean(v);
}

export function MovePhaseModal({
  open,
  onClose,
  project,
  phases,
  gate,
  initialTargetPhaseId,
}: {
  open: boolean;
  onClose: () => void;
  project: Project;
  phases: Phase[];
  /** #61: gated Übergang aus GET /v1/phases. Fehlt er, wird nichts gesperrt. */
  gate?: { from_phase: string; to_phase: string } | undefined;
  /** Board-Drop mit Pflichtfeld-Lücke (A8): Zielphase ist schon gewählt. */
  initialTargetPhaseId?: string | undefined;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useSession();

  const sortedPhases = useMemo(() => phases.slice().sort((a, b) => a.order - b.order), [phases]);
  const currentPhase = sortedPhases.find((p) => p.phase_id === project.phase_id);

  // #61: Der Eintritt in die gated Phase ist Admin und Kaufm. Leitung vorbehalten.
  // Wir bieten ihn gar nicht erst an, statt den Nutzer in einen 403 laufen zu lassen.
  // Das ist Bequemlichkeit, kein Schutz — durchgesetzt wird im Backend.
  const mayEnterGatedPhase = user ? canEnterGatedPhase(user) : false;
  const isGatedTarget = (phaseName: string): boolean =>
    !!gate && currentPhase?.name === gate.from_phase && phaseName === gate.to_phase;

  const [targetPhaseId, setTargetPhaseId] = useState<string>(initialTargetPhaseId ?? '');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [serverViolations, setServerViolations] = useState<FieldViolation[]>([]);

  const targetPhase = sortedPhases.find((p) => p.phase_id === targetPhaseId);

  // Pflichtfelder Zielphase, gefiltert auf "noch nicht gefüllt im Projekt"
  const pendingEntryFields = useMemo(() => {
    if (!targetPhase) return [] as string[];
    const required = targetPhase.entry_required_fields ?? [];
    return required.filter((path) => !isFilled(readPath(project, path)));
  }, [targetPhase, project]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!targetPhaseId) throw new Error('Keine Zielphase gewählt');

      // 1) Pflichtfelder patchen, falls etwas eingegeben wurde
      const patch: Record<string, unknown> = {};
      const handoverPatch: Record<string, unknown> = {
        ...(project.handover_ready_fields ?? {}),
      };
      const intakePatch: Record<string, unknown> = {
        ...(project.intake_handover_fields ?? {}),
      };
      let touched = false;
      let touchedHandover = false;
      let touchedIntake = false;
      for (const [path, raw] of Object.entries(fieldValues)) {
        const value = raw.trim();
        if (!value) continue;
        if (path.startsWith('handover_ready_fields.')) {
          handoverPatch[path.slice('handover_ready_fields.'.length)] = value;
          touchedHandover = true;
        } else if (path.startsWith('intake_handover_fields.')) {
          intakePatch[path.slice('intake_handover_fields.'.length)] = value;
          touchedIntake = true;
        } else {
          patch[path] = value;
          touched = true;
        }
      }
      if (touchedHandover) patch.handover_ready_fields = handoverPatch;
      if (touchedIntake) patch.intake_handover_fields = intakePatch;
      if (touched || touchedHandover || touchedIntake) {
        await api.patchProject(project.project_id, patch);
      }

      // 2) Phasenwechsel
      return api.movePhase(project.project_id, targetPhaseId);
    },
    onSuccess: () => {
      toast.success(
        t('v2.modal.move_phase_success', 'Phase gewechselt'),
        `→ ${targetPhase?.name ?? ''}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['v2-project', project.project_id] });
      void queryClient.invalidateQueries({ queryKey: ['v2-phases'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-board-projects'] });
      handleClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.isHardStop()) {
        setServerViolations(err.payload.errors);
        return;
      }
      toast.error(
        t('v2.board.move_failed', 'Phasenwechsel fehlgeschlagen'),
        err instanceof Error ? err.message : 'Unbekannter Fehler',
      );
    },
  });

  function handleClose() {
    setTargetPhaseId('');
    setFieldValues({});
    setServerViolations([]);
    mutation.reset();
    onClose();
  }

  const enterViolations = serverViolations.filter((v) => v.direction === 'enter');
  const leaveViolations = serverViolations.filter((v) => v.direction === 'leave');

  return (
    // Haelt getippte Pflichtfelder des Phasenwechsels — Hintergrundklick und Escape
    // duerfen sie nicht verwerfen. Geschlossen wird ueber „Abbrechen".
    <Modal
      open={open}
      onClose={handleClose}
      labelledBy="move-phase-title"
      panelClassName="w-full max-w-xl overflow-hidden"
      closeOnBackdrop={false}
      closeOnEscape={false}
    >
      <header
        className="px-5 py-4 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--line-soft)' }}
      >
        <div>
          <h2 id="move-phase-title" className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
            {t('v2.modal.move_phase_title', 'Phase wechseln')}
          </h2>
          <p
            className="text-[11px] mt-0.5"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.modal.move_phase_current', 'Aktuell: {{name}}', {
              name: currentPhase?.name ?? '—',
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="text-xs px-2 py-1 rounded"
          style={{ color: 'var(--ink-3)' }}
          aria-label={t('v2.briefings_lib.modal_close', 'Schließen')}
        >
          ✕
        </button>
      </header>

      <div className="px-5 py-4 space-y-4">
        <div>
          <label
            className="block text-[11px] uppercase tracking-[0.12em] mb-1.5"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.modal.move_phase_target', 'Zielphase')}
          </label>
          <select
            value={targetPhaseId}
            onChange={(e) => {
              setTargetPhaseId(e.target.value);
              setServerViolations([]);
            }}
            className="w-full text-sm px-3 py-2 rounded-md outline-none"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--line-soft)',
              color: 'var(--ink)',
            }}
          >
            <option value="">{t('v2.modal.move_phase_choose', '— wählen —')}</option>
            {sortedPhases.map((p) => {
              const blockedByGate = isGatedTarget(p.name) && !mayEnterGatedPhase;
              return (
                <option
                  key={p.phase_id}
                  value={p.phase_id}
                  disabled={p.phase_id === project.phase_id || blockedByGate}
                >
                  {String(p.order).padStart(2, '0')} · {p.name}
                  {blockedByGate
                    ? ` — ${t('v2.modal.move_phase_gate_hint', 'nur Kaufm. Leitung')}`
                    : ''}
                </option>
              );
            })}
          </select>
        </div>

        {targetPhase && pendingEntryFields.length > 0 && (
          <div
            className="rounded-md p-3"
            style={{ background: 'var(--warn-soft)', border: '1px solid var(--warn)' }}
          >
            <p
              className="text-[11px] uppercase tracking-[0.12em] mb-2"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--warn)' }}
            >
              {t('v2.modal.move_phase_required_for', 'Pflichtfelder für „{{name}}"', {
                name: targetPhase.name,
              })}
            </p>
            <div className="space-y-3">
              {pendingEntryFields.map((path) => (
                <FieldInput
                  key={path}
                  path={path}
                  value={fieldValues[path] ?? ''}
                  onChange={(v) => setFieldValues((prev) => ({ ...prev, [path]: v }))}
                  serverError={enterViolations.find((s) => s.field === path)}
                />
              ))}
            </div>
          </div>
        )}

        {leaveViolations.length > 0 && (
          <div
            className="rounded-md p-3 text-xs"
            style={{
              background: 'var(--danger-soft)',
              border: '1px solid var(--danger)',
              color: 'var(--danger)',
            }}
          >
            <p className="font-medium mb-1">
              {t(
                'v2.modal.move_phase_leave_blocked',
                'Pflichtfelder zum Verlassen der aktuellen Phase fehlen:',
              )}
            </p>
            <ul className="list-disc list-inside space-y-0.5">
              {leaveViolations.map((v) => (
                <li key={v.field}>{prettyField(v.field)}</li>
              ))}
            </ul>
            <p className="mt-2 opacity-80">
              {t(
                'v2.modal.move_phase_leave_hint',
                'Bitte zunächst die aktuelle Phase vervollständigen.',
              )}
            </p>
          </div>
        )}

        {targetPhase && pendingEntryFields.length === 0 && enterViolations.length === 0 && (
          <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
            {t(
              'v2.modal.move_phase_no_requirements',
              'Keine Pflichtfelder zum Betreten der Zielphase offen.',
            )}
          </p>
        )}
      </div>

      <footer
        className="px-5 py-3 border-t flex items-center justify-end gap-2"
        style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
      >
        <button
          type="button"
          onClick={handleClose}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
          }}
          disabled={mutation.isPending}
        >
          {t('v2.modal.cancel', 'Abbrechen')}
        </button>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!targetPhaseId || mutation.isPending}
          className="text-xs px-3 py-1.5 rounded-md transition-opacity"
          style={{
            background: 'var(--primary)',
            color: 'var(--on-primary)',
            border: 'none',
            opacity: !targetPhaseId || mutation.isPending ? 0.5 : 1,
          }}
        >
          {mutation.isPending
            ? t('v2.modal.move_phase_submitting', 'Wechselt…')
            : t('v2.modal.move_phase_submit', 'Phase wechseln →')}
        </button>
      </footer>
    </Modal>
  );
}

function FieldInput({
  path,
  value,
  onChange,
  serverError,
}: {
  path: string;
  value: string;
  onChange: (v: string) => void;
  serverError: FieldViolation | undefined;
}) {
  const label = prettyField(path);
  const isLong =
    path.startsWith('handover_ready_fields.') || path.startsWith('intake_handover_fields.');
  const isResponsibility = path === 'responsibility';

  return (
    <div>
      <label className="block text-[11px] mb-1" style={{ color: 'var(--ink-2)' }}>
        {label}
        {serverError && (
          <span className="ml-1.5 text-[10px]" style={{ color: 'var(--danger)' }}>
            · fehlt
          </span>
        )}
      </label>
      {isResponsibility ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm px-2 py-1.5 rounded outline-none"
          style={{
            background: 'var(--surface)',
            border: `1px solid ${serverError ? 'var(--danger)' : 'var(--line-soft)'}`,
            color: 'var(--ink)',
          }}
        >
          <option value="">— wählen —</option>
          <option value="PU">PU</option>
          <option value="Service">Service</option>
          <option value="MTB">MTB</option>
        </select>
      ) : isLong ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="w-full text-sm px-2 py-1.5 rounded outline-none resize-y"
          style={{
            background: 'var(--surface)',
            border: `1px solid ${serverError ? 'var(--danger)' : 'var(--line-soft)'}`,
            color: 'var(--ink)',
          }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm px-2 py-1.5 rounded outline-none"
          style={{
            background: 'var(--surface)',
            border: `1px solid ${serverError ? 'var(--danger)' : 'var(--line-soft)'}`,
            color: 'var(--ink)',
          }}
        />
      )}
    </div>
  );
}
